import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./CheckoutModal.css";
import { useCart } from "../context/CartContext";
import { useUser } from "../context/UserContext";
import DeliveryForm from "./DeliveryForm";
import { useApi } from "../utils/Api";
import ErrorModal from "./ErrorModal";
import GuestCheckoutModal from "./GuestCheckoutModal";

/* -------- Spinner Overlay -------- */
const SpinnerOverlay = () => (
  <div className="spinner-overlay">
    <div className="spinner"></div>
  </div>
);

const WHATSAPP_NUMBER = "447368325136";

function CheckoutModal({ onClose = () => {}, isOpen, checkoutType }) {
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  const [stage, setStage] = useState(0);
  const [selectedCheckoutType, setSelectedCheckoutType] = useState(
    checkoutType || null,
  );
  const [tableNumber, setTableNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { cartItems, clearCart } = useCart();
  const { user, openLoginModal } = useUser();

  const [encryptedOrderId, setEncryptedOrderId] = useState(null);

  // ✅ Guest checkout state (Eat-in only)
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [isGuest, setIsGuest] = useState(false);
  const [guestName, setGuestName] = useState("");

  // ✅ prevents showing guest modal multiple times
  const [guestChoiceMade, setGuestChoiceMade] = useState(false);

  // --- Service Charge ---
  const SERVICE_CHARGE_AMOUNT = 2.5;
  const [addServiceCharge, setAddServiceCharge] = useState(false);

  const wasOpenRef = useRef(false);

  const allowedServiceChargePhones = ["+447495891188"];
  const canAddServiceCharge =
    user && allowedServiceChargePhones.includes(user.phone);

  // Compute cart total
  const getItemTotals = (cartItem) => {
    const item = cartItem.item || cartItem;
    let total = Number(item.price || 0);

    if (cartItem.selectedRequiredOption) {
      total += Number(cartItem.selectedRequiredOption.price || 0);
    }

    [
      ...(item.swallowOptions || []),
      ...(item.proteins || []),
      ...(cartItem.selectedOptionalOptions || []),
    ].forEach((opt) => {
      total += Number(opt.option_price || 0) * (Number(opt.quantity) || 1);
    });

    return { total };
  };

  const cartTotal = cartItems.reduce(
    (acc, c) => acc + getItemTotals(c).total * (c.quantity || 1),
    0,
  );

  // Total including service charge
  const cartTotalWithService =
    cartTotal + (addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0);

  // Helper for full name
  const getUserFullName = () =>
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const getDisplayName = () => {
    if (user) return getUserFullName();
    return guestName.trim();
  };

  // ✅ Reset modal when closed + ✅ trigger guest modal immediately for desktop eat-in
  useEffect(() => {
    // detect open transition
    const justOpened = isOpen && !wasOpenRef.current;
    const justClosed = !isOpen && wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justClosed) {
      // reset only when it actually closes
      setStage(0);
      setSelectedCheckoutType(null);
      setTableNumber("");
      setNotes("");
      setSuccess(false);
      setIsLoading(false);
      setAddServiceCharge(false);

      // guest resets
      setShowGuestModal(false);
      setIsGuest(false);
      setGuestName("");
      setGuestChoiceMade(false);

      setEncryptedOrderId(null);
      return;
    }

    if (!justOpened) return; // ✅ IMPORTANT: don't re-init while open

    // modal JUST opened
    setSuccess(false);
    setIsLoading(false);

    if (checkoutType) {
      setSelectedCheckoutType(checkoutType);
      setStage(1);

      // eat-in can be guest: show guest modal immediately (desktop)
      if (checkoutType === "eat-in" && !user && !guestChoiceMade) {
        setShowGuestModal(true);
      }

      // pickup cannot be guest
      if (checkoutType === "pickup" && !user) {
        openLoginModal();
      }
    } else {
      // mobile: start at stage 0 only on first open
      setStage(0);
      setSelectedCheckoutType(null);
    }
  }, [isOpen, checkoutType, user, guestChoiceMade, openLoginModal]);

  useEffect(() => {
    if (user?.id) {
      setShowGuestModal(false);
      setIsGuest(false);
    }
  }, [user?.id]);

  const goBack = () => {
    if (stage > 1) setStage(1);
    else if (stage === 1 && !selectedCheckoutType) setStage(0);
  };

  const handleSelectOrderType = (type) => {
    setSelectedCheckoutType(type);
    setStage(1);

    // ❌ Pickup is NOT allowed as guest
    if (!user && type === "pickup") {
      openLoginModal();
      return;
    }

    // ✅ Eat-in: FIRST show guest modal
    if (!user && type === "eat-in" && !guestChoiceMade) {
      setShowGuestModal(true);
      return;
    }
  };

  const handleEatInStage1Answer = (answer) => {
    setStage(answer ? 2 : 3);
  };

  const placeEatInOrPickupOrder = async () => {
    if (isLoading) return;
    setIsLoading(true);

    if (!cartItems.length) {
      setIsLoading(false);
      return;
    }

    // ✅ Pickup must login (no guest)
    if (selectedCheckoutType === "pickup" && !user) {
      openLoginModal();
      setIsLoading(false);
      return;
    }

    // ✅ Eat-in: allow guest, but require they chose guest or login
    if (selectedCheckoutType === "eat-in" && !user && !isGuest) {
      setShowGuestModal(true);
      setIsLoading(false);
      return;
    }

    const payload = {
      customer_id: user?.id || null,
      guest_name: !user ? guestName.trim() : null,

      order_type: selectedCheckoutType === "eat-in" ? "Eat In" : "Pick Up",
      table_number:
        selectedCheckoutType === "eat-in" ? tableNumber.trim() : null,
      notes: notes.trim(),
      items: cartItems.map((c) => ({
        item_id: c.item?.id,
        base_price: c.item?.price || 0,
        required_option_id: c.selectedRequiredOption?.id || null,
        required_option_name: c.selectedRequiredOption?.name || "",
        required_option_price: c.selectedRequiredOption?.price || 0,
        quantity: c.quantity || 1,
        selectedOptionalOptions: c.selectedOptionalOptions || [],
        swallowOptions: c.item?.swallowOptions || [],
        proteins: c.item?.proteins || [],
      })),
      total_price: cartTotalWithService,
      service_charge: addServiceCharge ? SERVICE_CHARGE_AMOUNT : 0,
      status: "to do",
    };

    try {
      const result = await apiFetch("place-order", payload);

      if (result.success) {
        setEncryptedOrderId(result.encrypted_order_id);
        setSuccess(true);
        setTimeout(() => clearCart(), 1000);
      } else {
        console.error("Order failed:", result.error);
      }
    } catch (err) {
      console.error("Error sending order:", err);
    }

    setIsLoading(false);
  };

  // ✅ Validation
  const canSubmitEatIn =
    tableNumber.trim() !== "" &&
    !isLoading &&
    (user ? true : guestName.trim() !== "");

  const canSubmitPickup = !isLoading && !!user;

  // ---------- Immediate delivery block check ----------
  const BLOCKED_DELIVERY_IDS = [9, 11, 12, 16];
  const blockedDeliveryItem = cartItems.find((c) =>
    BLOCKED_DELIVERY_IDS.includes(Number(c.item?.category_id)),
  );
  const deliveryBlocked =
    selectedCheckoutType === "delivery" && stage === 1 && blockedDeliveryItem;

  if (!isOpen) return null;

  return (
    <div className="checkout-modal-overlay">
      <div className="checkout-modal" style={{ position: "relative" }}>
        {isLoading && <SpinnerOverlay />}

        <div className="checkout-modal-top modal-top">
          {stage > 1 && !success ? (
            <i className="fas fa-arrow-left back-icon" onClick={goBack}></i>
          ) : (
            <div className="back-placeholder"></div>
          )}
          <button
            className="add-to-cart-button close-modal-button"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <h2 className="checkout-heading">
          {!success && stage === 0 && "Select Order Type"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 1 &&
            "Eat In Checkout"}
          {!success &&
            selectedCheckoutType === "pickup" &&
            stage === 1 &&
            "Pick Up Checkout"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 2 &&
            "Customer Information"}
          {!success &&
            selectedCheckoutType === "eat-in" &&
            stage === 3 &&
            "Cannot Order"}
          {!success &&
            selectedCheckoutType === "delivery" &&
            stage === 1 &&
            "Delivery Checkout"}
          {success && "Order Received"}
        </h2>

        {/* Stage 0 */}
        {!success && stage === 0 && (
          <div className="stage-content">
            <h3>How would you like your order?</h3>
            <div className="button-group">
              <button
                className="add-to-cart-button"
                onClick={() => handleSelectOrderType("eat-in")}
              >
                Eat In
              </button>
              <button
                className="add-to-cart-button"
                onClick={() => handleSelectOrderType("pickup")}
              >
                Pick Up
              </button>
            </div>
          </div>
        )}

        {/* Eat-in Stage 1 */}
        {!success && selectedCheckoutType === "eat-in" && stage === 1 && (
          <div className="stage-content">
            <h3>Are you currently in the restaurant?</h3>
            <div className="button-group">
              <button
                className="add-to-cart-button"
                onClick={() => handleEatInStage1Answer(true)}
                disabled={!user && !isGuest} // ✅ ensures they pick guest/login first
              >
                Yes
              </button>
              <button
                className="add-to-cart-button"
                onClick={() => handleEatInStage1Answer(false)}
                disabled={!user && !isGuest} // ✅ ensures they pick guest/login first
              >
                No
              </button>
            </div>
          </div>
        )}

        {/* Eat-in Stage 2 */}
        {!success && selectedCheckoutType === "eat-in" && stage === 2 && (
          <form
            className="checkout-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmitEatIn) placeEatInOrPickupOrder();
            }}
          >
            <label>Your Name</label>

            {user ? (
              <input type="text" value={getUserFullName()} readOnly required />
            ) : (
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter your name"
                required
              />
            )}

            <label>Table Number</label>
            <input
              type="text"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              required
            />

            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            {canAddServiceCharge && (
              <div className="service-charge-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={addServiceCharge}
                    onChange={() => setAddServiceCharge(!addServiceCharge)}
                  />
                  <span className="slider round"></span>
                </label>
                <span style={{ marginLeft: "10px" }}>
                  Service Charge (£{SERVICE_CHARGE_AMOUNT.toFixed(2)})
                </span>
              </div>
            )}

            {addServiceCharge && (
              <div className="price-breakdown" style={{ margin: "1rem 0" }}>
                <div className="price-row">
                  <span>Order Price</span>
                  <span>£{cartTotal.toFixed(2)}</span>
                </div>

                <div className="price-row">
                  <span>Service Charge</span>
                  <span>£{SERVICE_CHARGE_AMOUNT.toFixed(2)}</span>
                </div>

                <hr />

                <div className="price-row total">
                  <span>
                    <strong>Total</strong>
                  </span>
                  <span>
                    <strong>£{cartTotalWithService.toFixed(2)}</strong>
                  </span>
                </div>
              </div>
            )}

            <button
              type="submit"
              className="add-to-cart-button send-button"
              disabled={!canSubmitEatIn}
            >
              Send Order To Kitchen
              <span style={{ marginLeft: "0.9rem", fontWeight: "bold" }}>
                £{cartTotalWithService.toFixed(2)}
              </span>
            </button>
          </form>
        )}

        {!success && selectedCheckoutType === "eat-in" && stage === 3 && (
          <div className="cannot-order-message">
            <p>You must be in the restaurant to place an Eat-In order.</p>
          </div>
        )}

        {/* Pickup Stage */}
        {!success && selectedCheckoutType === "pickup" && stage === 1 && (
          <form
            className="checkout-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmitPickup) placeEatInOrPickupOrder();
              else openLoginModal();
            }}
          >
            <label>Your Name</label>
            <input type="text" value={getDisplayName()} readOnly required />

            <label>Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <button
              type="submit"
              className="add-to-cart-button send-button"
              disabled={!canSubmitPickup}
            >
              Send Order To Kitchen
              <span style={{ marginLeft: "0.9rem", fontWeight: "bold" }}>
                £{cartTotalWithService.toFixed(2)}
              </span>
            </button>
          </form>
        )}

        {/* Delivery Stage */}
        {!success &&
          selectedCheckoutType === "delivery" &&
          stage === 1 &&
          !deliveryBlocked && (
            <DeliveryForm
              notes={notes}
              setNotes={setNotes}
              setIsLoading={setIsLoading}
              onClose={onClose}
            />
          )}

        {/* Success */}
        {success && (
          <div className="order-success">
            <h3>Your order has been received and is being processed.</h3>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-support-link"
            >
              <i className="fab fa-whatsapp"></i>
              Click to chat with us on WhatsApp
            </a>

            <button
              className="add-to-cart-button"
              onClick={() => {
                if (encryptedOrderId) {
                  navigate(`/order-tracking?id=${encryptedOrderId}`);
                }
                onClose();
              }}
            >
              Click here to track your order
            </button>
          </div>
        )}

        {/* Error Modal */}
        {deliveryBlocked && (
          <ErrorModal
            message="Alcoholic drinks cannot be delivered. Please remove them to proceed with delivery."
            onClose={() => {
              setSelectedCheckoutType(null);
              onClose();
            }}
          />
        )}

        {/* ✅ Guest modal (Eat-in only, FIRST stage) */}
        <GuestCheckoutModal
          isOpen={showGuestModal}
          onClose={() => {
            // ✅ only close the guest modal (do NOT close the whole checkout modal)
            setShowGuestModal(false);
          }}
          onContinueGuest={() => {
            setIsGuest(true);
            setGuestChoiceMade(true);
            setShowGuestModal(false);

            setSelectedCheckoutType("eat-in");
            setStage(1);
          }}
          onLogin={() => {
            setGuestChoiceMade(true);
            setShowGuestModal(false);
            openLoginModal();
          }}
        />
      </div>
    </div>
  );
}

export default CheckoutModal;
